import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useReveal } from "@/lib/motion";
import { AutoScroller } from "@/components/AutoScroller";

export const AndreettaCase = ({ client }) => {
    const reveal = useReveal();

    // Split the 12 regular client posts into 2 rows of 6
    const posts = client.posts || [];
    const row1 = posts.slice(0, 6);
    const row2 = posts.slice(6, 12);

    const basePath = "/images/clientes/andreetta-contabilidade/case-sucesso";

    const socialPosts = [
        `${basePath}/post-palestra-1.png`,
        `${basePath}/post-palestra-2.png`,
        `${basePath}/post-palestra-3.png`,
        `${basePath}/post-palestra-4.png`,
    ];

    const emailTemplates = [
        `${basePath}/email-1.png`,
        `${basePath}/email-2.png`,
        `${basePath}/email-3.png`,
        `${basePath}/email-4.png`,
    ];

    const whatsappCards = [
        `${basePath}/whatsapp-1.png`,
        `${basePath}/whatsapp-2.png`,
        `${basePath}/whatsapp-3.png`,
        `${basePath}/whatsapp-4.png`,
        `${basePath}/whatsapp-5.png`,
    ];

    return (
        <article className="min-h-screen bg-white pb-28 pt-8 md:pt-12 overflow-x-hidden">
            {/* Section 1: Title & Institutional Summary */}
            <div className="mx-auto max-w-[1240px] px-6 sm:px-8 lg:px-12">
                <motion.h1
                    className="type-display text-brand text-[42px] sm:text-[54px] md:text-[64px] font-bold leading-tight tracking-tight"
                    {...reveal()}
                >
                    {client.name}
                </motion.h1>

                <motion.div
                    className="mt-6 md:mt-8 max-w-[760px] space-y-4 text-[15px] sm:text-[16.5px] leading-relaxed text-[#333333] font-sans"
                    {...reveal(0.06)}
                >
                    {Array.isArray(client.summary) ? (
                        client.summary.map((para, i) => <p key={i}>{para}</p>)
                    ) : (
                        <p>{client.summary}</p>
                    )}
                </motion.div>
            </div>

            {/* Section 2: Two-Row Carousel of Client Posts */}
            {posts.length > 0 && (
                <motion.div className="mt-12 md:mt-16 w-full space-y-6 bg-white py-2" {...reveal(0.1)}>
                    {/* Row 1 */}
                    <AutoScroller ariaLabel="Publicações Andreetta Contabilidade - Linha 1">
                        {row1.map((src, index) => (
                            <div
                                key={`row1-${index}`}
                                className="aspect-[4/5] w-[210px] sm:w-[250px] md:w-[280px] shrink-0 overflow-hidden rounded-[10px] sm:rounded-[12px] bg-white shadow-[0_6px_20px_rgba(0,0,0,0.12)] border border-black/5 transition-transform duration-300 hover:scale-[1.03]"
                            >
                                <img
                                    src={src}
                                    alt={`Publicação ${index + 1}`}
                                    className="h-full w-full object-cover pointer-events-none select-none"
                                    loading="lazy"
                                    draggable="false"
                                />
                            </div>
                        ))}
                    </AutoScroller>

                    {/* Row 2 (Reverse direction) */}
                    {row2.length > 0 && (
                        <AutoScroller reverse ariaLabel="Publicações Andreetta Contabilidade - Linha 2">
                            {row2.map((src, index) => (
                                <div
                                    key={`row2-${index}`}
                                    className="aspect-[4/5] w-[210px] sm:w-[250px] md:w-[280px] shrink-0 overflow-hidden rounded-[10px] sm:rounded-[12px] bg-white shadow-[0_6px_20px_rgba(0,0,0,0.12)] border border-black/5 transition-transform duration-300 hover:scale-[1.03]"
                                >
                                    <img
                                        src={src}
                                        alt={`Publicação ${index + 7}`}
                                        className="h-full w-full object-cover pointer-events-none select-none"
                                        loading="lazy"
                                        draggable="false"
                                    />
                                </div>
                            ))}
                        </AutoScroller>
                    )}
                </motion.div>
            )}

            {/* Section 3: Case de sucesso - Palestra Reforma Tributária */}
            <div className="mx-auto max-w-[1240px] px-6 sm:px-8 lg:px-12 mt-20 md:mt-28">
                <motion.h2
                    className="type-display text-brand text-[32px] sm:text-[42px] md:text-[48px] font-bold leading-tight"
                    {...reveal()}
                >
                    Case de sucesso - Palestra Reforma Tributária
                </motion.h2>

                <div className="mt-8 md:mt-10 grid grid-cols-1 lg:grid-cols-[1fr_1.05fr] gap-10 lg:gap-14 items-center">
                    {/* Left: Description paragraphs */}
                    <motion.div
                        className="space-y-4 text-[15px] sm:text-[16.5px] leading-relaxed text-[#333333] font-sans"
                        {...reveal(0.06)}
                    >
                        <p>
                            Para a Andreetta Contabilidade, desenvolvemos toda a comunicação de uma palestra sobre Reforma Tributária, acompanhando o projeto desde a divulgação até a realização do evento. A estratégia envolveu a criação de diferentes pontos de contato para alcançar o público, gerar interesse e facilitar as inscrições.
                        </p>
                        <p>
                            Desenvolvemos os materiais para Instagram, cards para WhatsApp, e-mail marketing e vídeo de divulgação, além de três banners utilizados na comunicação do evento. Também criamos a landing page responsável pelo cadastro e confirmação de presença dos participantes, centralizando o processo de inscrição.
                        </p>
                        <p>
                            No dia da palestra, estivemos presentes realizando a cobertura do evento e a captação dos conteúdos, registrando os principais momentos para gerar materiais que também pudessem ser utilizados posteriormente nas redes sociais.
                        </p>
                        <p>
                            Um projeto completo de comunicação, pensado para conectar divulgação, conversão e experiência presencial em uma mesma estratégia.
                        </p>
                    </motion.div>

                    {/* Right: Featured cards showcase layout */}
                    <motion.div className="flex flex-col gap-4 sm:gap-5" {...reveal(0.1)}>
                        {/* Top banner card */}
                        <div className="w-full aspect-[16/9] sm:aspect-[2/1] rounded-[16px] md:rounded-[20px] overflow-hidden shadow-xl border border-black/5 bg-black/5">
                            <img
                                src={`${basePath}/kv-oficial.png`}
                                alt="Reforma Tributária - Banner Destaque"
                                className="w-full h-full object-cover"
                            />
                        </div>

                        {/* Bottom 2 side-by-side cards */}
                        <div className="grid grid-cols-2 gap-4 sm:gap-5">
                            <div className="aspect-[4/5] rounded-[12px] sm:rounded-[16px] overflow-hidden shadow-lg border border-black/5 bg-black/5">
                                <img
                                    src={`${basePath}/post-palestra-2.png`}
                                    alt="Reforma Tributária - Post 1"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="aspect-[4/5] rounded-[12px] sm:rounded-[16px] overflow-hidden shadow-lg border border-black/5 bg-black/5">
                                <img
                                    src={`${basePath}/post-palestra-3.png`}
                                    alt="Reforma Tributária - Post 2"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Section 4: Posts Redes Sociais */}
            <div className="mx-auto max-w-[1240px] px-6 sm:px-8 lg:px-12 mt-16 md:mt-24">
                <motion.div {...reveal()}>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                        {socialPosts.map((src, i) => (
                            <div
                                key={i}
                                className="aspect-[4/5] rounded-[10px] sm:rounded-[14px] overflow-hidden shadow-lg border border-black/5 bg-white transition-transform duration-300 hover:scale-[1.02]"
                            >
                                <img
                                    src={src}
                                    alt={`Post Redes Sociais ${i + 1}`}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                        ))}
                    </div>
                    <p className="mt-3 sm:mt-4 text-right text-[13.5px] sm:text-[15px] text-[#555555] font-medium">
                        Posts Redes Sociais
                    </p>
                </motion.div>
            </div>

            {/* Section 5: E-mail Marketing */}
            <div className="mx-auto max-w-[1240px] px-6 sm:px-8 lg:px-12 mt-16 md:mt-24">
                <motion.div {...reveal()}>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                        {emailTemplates.map((src, i) => (
                            <div
                                key={i}
                                className="aspect-[600/1591] rounded-[10px] sm:rounded-[14px] overflow-hidden shadow-lg border border-black/5 bg-white transition-transform duration-300 hover:scale-[1.02]"
                            >
                                <img
                                    src={src}
                                    alt={`E-mail Marketing ${i + 1}`}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                        ))}
                    </div>
                    <p className="mt-3 sm:mt-4 text-left text-[13.5px] sm:text-[15px] text-[#555555] font-medium">
                        E-mail Marketing
                    </p>
                </motion.div>
            </div>

            {/* Section 6: KV Oficial */}
            <div className="mx-auto max-w-[1240px] px-6 sm:px-8 lg:px-12 mt-16 md:mt-24">
                <motion.div {...reveal()}>
                    <div className="w-full aspect-[1600/838] rounded-[16px] md:rounded-[24px] overflow-hidden shadow-2xl border border-black/5 bg-white">
                        <img
                            src={`${basePath}/kv-oficial.png`}
                            alt="KV Oficial Reforma Tributária"
                            className="w-full h-full object-cover"
                            loading="lazy"
                        />
                    </div>
                    <p className="mt-3 text-left text-[13.5px] sm:text-[15px] text-[#555555] font-medium">
                        KV Oficial
                    </p>
                </motion.div>
            </div>

            {/* Section 7: Cards de Whatsapp */}
            <div className="mx-auto max-w-[1240px] px-6 sm:px-8 lg:px-12 mt-16 md:mt-24">
                <motion.div {...reveal()}>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 sm:gap-5">
                        {whatsappCards.map((src, i) => (
                            <div
                                key={i}
                                className="aspect-[9/16] rounded-[10px] sm:rounded-[14px] overflow-hidden shadow-lg border border-black/5 bg-white transition-transform duration-300 hover:scale-[1.02]"
                            >
                                <img
                                    src={src}
                                    alt={`Card WhatsApp ${i + 1}`}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                        ))}
                    </div>
                    <p className="mt-3 text-left text-[13.5px] sm:text-[15px] text-[#555555] font-medium">
                        Cards de Whatsapp
                    </p>
                </motion.div>
            </div>

            {/* Section 8: O resultado & CTA */}
            <div className="mx-auto max-w-[1240px] px-6 sm:px-8 lg:px-12 mt-20 md:mt-28">
                <motion.div
                    className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr] gap-10 md:gap-14 items-center"
                    {...reveal()}
                >
                    {/* Left: Event Photo */}
                    <div className="w-full aspect-[4/3] rounded-[18px] md:rounded-[24px] overflow-hidden shadow-2xl border border-black/5 bg-white">
                        <img
                            src={`${basePath}/resultado-evento.png`}
                            alt="Palestra Reforma Tributária - Evento Presencial"
                            className="w-full h-full object-cover"
                        />
                    </div>

                    {/* Right: Text & CTA */}
                    <div className="flex flex-col items-start">
                        <h3 className="type-display text-brand text-[36px] sm:text-[44px] font-bold leading-tight">
                            O resultado
                        </h3>
                        <p className="mt-4 text-[15.5px] sm:text-[17px] leading-relaxed text-[#333333] font-sans">
                            O resultado foi uma entrega completa, que uniu estratégia, design, conteúdo e produção para transformar uma iniciativa pontual em uma <strong className="font-bold text-ink">experiência de marca</strong>.
                        </p>
                        <Link
                            to="/contato"
                            className="inline-flex items-center justify-center bg-brand hover:bg-brand-deep text-white font-bold uppercase tracking-wider text-[14px] sm:text-[15px] px-8 sm:px-10 py-3.5 sm:py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 mt-8"
                        >
                            SOLICITE UM ORÇAMENTO!
                        </Link>
                    </div>
                </motion.div>
            </div>
        </article>
    );
};
